import { MatDialog } from '@angular/material/dialog';
import { HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subscription } from 'rxjs';

import * as moment from 'moment';

import {
    ApiError,
    ApiResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ClientStatusesEnum,
    ReportsService,
    IndividualReportInterface,
    SearchUsersExportFilterInterface,
    ExportableFilterHeaderColumnsInterface,
    PortalModel,
    LookupModel,
    CountryModel,
    IndividualFilterModel,
    IndividualReportModel,
    SearchUsersExportFilterModel, TradingPlatformInfoModel,
} from '@spa/api';

import {EnumsHelper, FilterControl, Helper} from '../../../shared/classes';
import { AlertsService, CacheCollectionService } from '../../../shared/services';
import { Pagination, ReportTableColumn, ReportTableData, ReportTableDataRow } from '../../../shared/components';

import { PanelPaths } from 'appShared/paths';
import { PermissionRuleInterface } from 'appShared/interfaces';
import { InitialComponentDataValidationException } from 'appShared/exceptions';
import {
    AlertTypesEnum,
    FileExtensionsEnum,
    PermissionRuleCheckStrategiesEnum,
    PermissionsEnum
} from 'appShared/enums';

import { EventsService } from '../services';
import { ClientEntitiesReportResolverDataInterface, SortColumnInterface } from '../interfaces';
import { ClientEntitiesResolverErrorStatesEnum, EnrollTypesEnum } from '../enums';

import { ReportComponent } from './report-component';

import { ClientEntitiesMessages } from 'appAssets/messages';

import {
    ClientsReportRowIdSeparator,
    CompaniesPathSelector,
    IndividualPathSelector
} from '../../../../assets/descriptors/panel-clients';

import { environment } from 'appEnv/environment';
import { FilterModel } from '@spa/api/lib/common';

export abstract class ClientEntitiesReportComponent
    extends ReportComponent<IndividualReportInterface, IndividualReportModel> {
    private static readonly MifirIndividualExportFileName = 'import_individual_clients.csv';
    private static readonly MifirCorporateExportFileName = 'import_corporate_clients.csv';
    private static readonly NoRecordsToExportMessage = 'No records to export.';

    private activatedRouteSubscription: Subscription;
    private getExportFilterDataSubscription: Subscription;
    private readonly mifirExportSubscriptions: Subscription;
    private countries: CountryModel[];
    private individualStatusesCollection: LookupModel;
    private individualInitialFilter: IndividualFilterModel;
    private visibleTableColumns: ReportTableColumn[];
    private portals: PortalModel[];
    private tradingPlatforms: TradingPlatformInfoModel[];

    protected dialog: MatDialog;
    protected activatedRouter: ActivatedRoute;
    protected reportsService: ReportsService;
    protected alertsService: AlertsService;

    public panelPaths: typeof PanelPaths;
    public visibleExportButtonRule: PermissionRuleInterface;
    public mifirIndividualExportEnabled: boolean;
    public mifirCorporateExportEnabled: boolean;
    public mifirIndividualExportLoading: boolean;
    public mifirCorporateExportLoading: boolean;

    protected constructor(
        dialog: MatDialog,
        activatedRouter: ActivatedRoute,
        reportsService: ReportsService,
        router: Router,
        eventsService: EventsService,
        cacheCollectionService: CacheCollectionService,
        alertsService: AlertsService
    ) {
        super(router, eventsService, cacheCollectionService);

        this.dialog = dialog;
        this.activatedRouter = activatedRouter;
        this.reportsService = reportsService;
        this.alertsService = alertsService;

        this.portals = [];
        this.countries = [];
        this.modelsCollection = [];
        this.visibleTableColumns = [];
        this.mifirExportSubscriptions = new Subscription();
        this.individualStatusesCollection = null;
        this.filterModel = this.getInitialFilterModel();
        this.panelPaths = PanelPaths;
        this.mifirIndividualExportEnabled = false;
        this.mifirCorporateExportEnabled = false;
        this.mifirIndividualExportLoading = false;
        this.mifirCorporateExportLoading = false;
    }

    private static getDownloadFileName(headers: HttpHeaders, fallbackFileName: string): string {
        const contentDisposition = headers?.get('content-disposition');

        if (!contentDisposition) {
            return fallbackFileName;
        }

        const encodedFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

        if (encodedFileName?.[1]) {
            return decodeURIComponent(encodedFileName[1].replace(/"/g, ''));
        }

        const fileName = contentDisposition.match(/filename="?([^\";]+)"?/i);

        return fileName?.[1] ? fileName[1] : fallbackFileName;
    }

    private getActiveClientExportFilter(): SearchUsersExportFilterInterface {
        const exportFilter = {} as SearchUsersExportFilterInterface;

        const needlessKeys = [
            IndividualFilterModel.getPageSizeSelector(),
            IndividualFilterModel.getPageNumberSelector(),
            IndividualFilterModel.getOrderColumnSelector(),
            IndividualFilterModel.getOrderAscendingSelector()
        ];

        Object.keys(this.filterModel).forEach((key: string) => {
            if (!needlessKeys.includes(key)) {
                exportFilter[key] = this.isFilterChanged ? this.filterModel[key] : this.individualInitialFilter[key];
            }
        });

        return exportFilter;
    }

    private setMifirExportLoadingState(enrollType: EnrollTypesEnum, loading: boolean): void {
        this.mifirIndividualExportLoading = (enrollType === EnrollTypesEnum.Individual) ? loading : this.mifirIndividualExportLoading;
        this.mifirCorporateExportLoading = (enrollType === EnrollTypesEnum.Corporate) ? loading : this.mifirCorporateExportLoading;
    }

    private triggerMifirClientsExport(enrollType: EnrollTypesEnum): void {
        const exportConfig = {
            [EnrollTypesEnum.Individual]: {
                enabled: this.mifirIndividualExportEnabled,
                path: 'report/mifir/individuals/export',
                fileName: ClientEntitiesReportComponent.MifirIndividualExportFileName
            },
            [EnrollTypesEnum.Corporate]: {
                enabled: this.mifirCorporateExportEnabled,
                path: 'report/mifir/corporate/export',
                fileName: ClientEntitiesReportComponent.MifirCorporateExportFileName
            }
        }[enrollType];

        if (!exportConfig?.enabled) {
            return;
        }

        this.setMifirExportLoadingState(enrollType, true);

        const request = (this.reportsService as any).postRequest(
            exportConfig.path,
            { filter: this.getActiveClientExportFilter() },
            false,
            true
        ).subscribe((response: ServerResponseData<Blob>) => {
            this.setMifirExportLoadingState(enrollType, false);

            if ((response.status === HttpStatusCodesEnum.Ok) && response.data) {
                const fileName = ClientEntitiesReportComponent.getDownloadFileName(response.headers, exportConfig.fileName);

                Helper.filesHelper().triggerDownloadFile(fileName, response.data as Blob);

                return;
            }

            if ((response.status === HttpStatusCodesEnum.Ok) || (response.status === HttpStatusCodesEnum.NotFound)) {
                this.alertsService.addAlert(
                    ClientEntitiesReportComponent.NoRecordsToExportMessage,
                    AlertTypesEnum.Warning
                );

                return;
            }

            this.alertsService.addAlert(ClientEntitiesMessages.UnknownError.message);
        });

        this.mifirExportSubscriptions.add(request);
    }

    protected updateMifirExportAvailability(filterData?: { [key: string]: unknown }): void {
        const selectedEnrollType = filterData
            ? filterData[IndividualFilterModel.getEnrollTypeSelector()]
            : this.filterControls
                .find(control => control.name === IndividualFilterModel.getEnrollTypeSelector())
                ?.controls[0]?.value;

        this.mifirIndividualExportEnabled = selectedEnrollType === EnrollTypesEnum.Individual;
        this.mifirCorporateExportEnabled = selectedEnrollType === EnrollTypesEnum.Corporate;
    }

    public getMifirIndividualExportDisabledReason(): string | null {
        if (this.mifirIndividualExportEnabled || this.mifirIndividualExportLoading) {
            return null;
        }

        return 'Unavailable. Set Client Type to Individual. This stays disabled when Client Type is Corporate or All.';
    }

    public getMifirCorporateExportDisabledReason(): string | null {
        if (this.mifirCorporateExportEnabled || this.mifirCorporateExportLoading) {
            return null;
        }

        return 'Unavailable. Set Client Type to Corporate. This stays disabled when Client Type is Individual or All.';
    }

    protected fixSortColumnSelector(columnSelector: string): string {
        switch (columnSelector) {
            case IndividualReportModel.getStatusNameSelector():
                return IndividualReportModel.getStatusSelector();
            case IndividualReportModel.getNameSelector():
                return IndividualReportModel.getFirstNameSelector();
            case IndividualReportModel.getCountryNameSelector():
                return IndividualReportModel.getCountryIdSelector();
            case IndividualReportModel.getResidentialCountryNameSelector():
                return IndividualReportModel.getResidentialCountryIdSelector();
            default:
                return columnSelector;
        }
    }
    protected fixLoadSortSelector(columnSelector: string): string {
        switch (columnSelector) {
            case IndividualReportModel.getFirstNameSelector():
                return IndividualReportModel.getNameSelector();
            case IndividualReportModel.getStatusSelector():
                return IndividualReportModel.getStatusNameSelector();
            case IndividualReportModel.getCountryIdSelector():
                return IndividualReportModel.getCountryNameSelector();
            case IndividualReportModel.getResidentialCountryIdSelector():
                return IndividualReportModel.getResidentialCountryNameSelector();
            default:
                return columnSelector;
        }
    }

    abstract getDefaultIndividualStatuses(): number[];

    abstract getDataSourceItems(): ReportTableData[];

    onInit(): void {
        super.onInit();
        // tslint:disable-next-line:max-line-length
        const sortedColumnCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data as FilterModel;
        // tslint:disable-next-line:max-line-length
        this.sortedColumn = sortedColumnCache ? { selector: this.fixLoadSortSelector(sortedColumnCache.order_column), ascending: sortedColumnCache.order_ascending } : { selector: null, ascending: false };

        this.activatedRouteSubscription = this.activatedRouter.data
            .subscribe((result: { data: ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(result.data, () => {
                    const {
                        portals,
                        countries,
                        filterControls,
                        dataTableConfig,
                        modelsCollection,
                        statusCollection,
                        individualInitialFilter,
                        tradingPlatforms
                    } = (result.data.data as ClientEntitiesReportResolverDataInterface);

                    this.portals = portals;
                    this.countries = countries;
                    this.filterControls = filterControls;
                    this.dataTableConfig = dataTableConfig;
                    this.modelsCollection = modelsCollection;
                    this.individualStatusesCollection = statusCollection;
                    this.individualInitialFilter = individualInitialFilter;
                    this.tradingPlatforms = tradingPlatforms;
                    const start = Pagination.calculateStartPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const end = Pagination.calculateEndPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const paginatedCollection = this.paginateCollection<IndividualReportModel>(
                        this.modelsCollection, start, end
                    );

                    this.paginationConfig.totalItems = this.modelsCollection.length;

                    this.dataTableConfig.rows = this.createDataSource(paginatedCollection);

                    this.visibleTableColumns = this.dataTableConfig.columns;

                    this.visibleExportButtonRule = {
                        permissions: [
                            PermissionsEnum.CrmClientsExport,
                            PermissionsEnum.CrmClientsViewDetails,
                            PermissionsEnum.CRMReportsViewMarketingReports],
                        strategy: PermissionRuleCheckStrategiesEnum.Union
                    };
                });
            });
    }

    onDestroy(): void {
        this.activatedRouteSubscription.unsubscribe();

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }

        if (this.getExportFilterDataSubscription) {
            this.getExportFilterDataSubscription.unsubscribe();
        }

        this.mifirExportSubscriptions.unsubscribe();
    }

    getReportHandler(filter: IndividualFilterModel): Observable<ApiResponse<ApiError[] | IndividualReportInterface[]>> {
        return this.reportsService.getIndividualsReport(filter);
    }

    getInitialFilterModel(): IndividualFilterModel {
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk();

        return new IndividualFilterModel(
            null,
            null,
            null,
            null,
            this.getDefaultIndividualStatuses(),
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            IndividualFilterModel.getEnrollDateToSelector(),
            false,
            null,
            null
        );
    }

    setFilterControlValues(status: number): void {
        this.filterControls.map((filterControl: FilterControl) => {
            if ((status === null) && (filterControl.name === IndividualFilterModel.getStatusesSelector())) {
                filterControl.setControlValueByName(
                    IndividualFilterModel.getStatusesSelector(),
                    ClientStatusesEnum.All
                );
            }
        });
    }

    mapFilterResults(results: IndividualReportInterface[]): IndividualReportModel[] {
        return results.map((item: IndividualReportInterface) => {
            const model = new IndividualReportModel(item);

            model.statusName = '';
            model.countryName = '';
            model.enrollDate = Helper.formatDate(model.enrollDate);
            model.verificationDate = Helper.formatDate(model.verificationDate);
            model.typeBehaviorName = Helper.textsHelper().getUserTypeBehaviourName(model.typeBehavior);

            const filteredStatusKeys = this.individualStatusesCollection.getTypeKeys()
                .filter((status: string) => {
                    return parseInt(status, 10) === model.status;
                });

            if (filteredStatusKeys.length !== 0) {
                model.statusName = this.individualStatusesCollection.getTypeValue(filteredStatusKeys[0]);
            }

            const filteredCountries = this.countries.filter((country: CountryModel) => {
                return country.id === model.countryId;
            });

            if (filteredCountries.length !== 0) {
                model.countryName = filteredCountries[0].name;
            }

            model.statusGroup = EnumsHelper.getStatusGroupName(model.status);


            model.portalNames = this.portals
                .filter((portal: PortalModel) => model.portalIds?.length > 0 && model.portalIds.includes(portal.id))
                .map((portal: PortalModel) => portal.name)
                .join(', ');

            model.residentialCountryName = this.countries.find((country: CountryModel) => country.id === model.residentialCountryId)?.name;
            model.enrollTradingPlatformName = this.tradingPlatforms.find((platform: TradingPlatformInfoModel) =>
                platform.id === model.enrollTradingPlatformId)?.name;
            model.platformSubscriptionList = model.platformSubscriptions
                .map(sub => {
                    const platform = this.tradingPlatforms.find(p => p.id === sub.trading_platform_id);
                    if (!platform) { return null; }

                    if (sub.trading_platform_version_id && sub.trading_platform_version_id > 0) {
                        const version = platform.trading_platform_versions
                            .find(v => v.id === sub.trading_platform_version_id);

                        return version?.name ?? platform.name;
                    }

                    return platform.name;
                })
                .filter((name): name is string => Boolean(name)).join(', ');

            return model;
        });
    }

    createDataSource(data: IndividualReportModel[]): ReportTableDataRow[] {
        return data.map((item: IndividualReportModel) => {
            const row = new ReportTableDataRow({
                rowId: (item.enrollId + ClientsReportRowIdSeparator + item.enrollType),
                dataItems: []
            });

            row.dataItems = this.getDataSourceItems().map((dataTableItem: ReportTableData) => {
                switch (dataTableItem.colId) {
                    case IndividualReportModel.getEnrollTypeSelector():
                        dataTableItem.value = Helper.textsHelper().getEnrollTypeName(item.enrollType);
                        break;
                    case IndividualReportModel.getEnrollDateSelector():
                        dataTableItem.value = item.enrollDate;
                        break;
                    case IndividualReportModel.getVerificationDateSelector():
                        dataTableItem.value = item.verificationDate;
                        break;
                    case IndividualReportModel.getStatusNameSelector():
                        dataTableItem.value = item.statusName;
                        dataTableItem.class = `client-status-${item.statusName.toLowerCase().replace(' ', '-')}`;
                        break;
                    case IndividualReportModel.getFirstNameSelector():
                        dataTableItem.value = item.firstName;
                        break;
                    case IndividualReportModel.getLastNameSelector():
                        dataTableItem.value = item.lastName;
                        break;
                    case IndividualReportModel.getNameSelector():
                        dataTableItem.value = item.name;
                        break;
                    case IndividualReportModel.getCountryNameSelector():
                        dataTableItem.value = item.countryName;
                        break;
                    case IndividualReportModel.getEmailSelector():
                        dataTableItem.value = item.email;
                        break;
                    case IndividualReportModel.getEnrollPortalNameSelector():
                        dataTableItem.value = item.enrollPortalName;
                        break;
                    case IndividualReportModel.getBirthDateSelector():
                        dataTableItem.value = item.birth_date ? moment(item.birth_date).format(environment.dateFormatLocalDate)  : '';
                        break;
                    case IndividualReportModel.getLeiCodeSelector():
                        dataTableItem.value = item.lei_code;
                        break;
                    case IndividualReportModel.getMifirNumberSelector():
                        dataTableItem.value = item.mifirNumber;
                        break;
                    case IndividualReportModel.getMifirTypeSelector():
                        dataTableItem.value = Helper.textsHelper().getMiFirTypeName(item.mifir_type);
                        break;
                    case IndividualReportModel.getNationalitiesSelector():
                        dataTableItem.value = item.nationalities;
                        break;
                    case IndividualReportModel.getOutcomeClassificationSelector():
                        dataTableItem.value = !item.outcomeClassification ? 'N/A' : item.outcomeClassification;
                        break;
                    case IndividualReportModel.getRolesSelector():
                        dataTableItem.value = item.roles.join(', ').trim();
                        break;
                    case IndividualReportModel.getEmailConfirmedSelector():
                        dataTableItem.value = !item.emailConfirmed ? 'No' : 'Yes';
                        break;
                    case IndividualReportModel.getOldClientIdSelector():
                        dataTableItem.value = item.old_client_id;
                        break;
                    case IndividualReportModel.getStatusGroupSelector():
                        dataTableItem.value = item.statusGroup;
                        break;
                    case IndividualReportModel.getEnrollTradingPlatformNameSelector():
                        dataTableItem.value = item.enrollTradingPlatformName;
                        break;
                    case IndividualReportModel.getResidentialCountryNameSelector():
                        dataTableItem.value = item.residentialCountryName;
                        break;
                    case IndividualReportModel.getResidentialCitySelector():
                        dataTableItem.value = item.residentialCity;
                        break;
                    case IndividualReportModel.getPlatformSubscriptionListSelector():
                        dataTableItem.value = item.platformSubscriptionList;
                        break;
                }

                return dataTableItem;
            });

            return row;
        });
    }

    validateInitialData(
        response: ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>
    ): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const messages = (response.data as ApiError[]).map((error: ApiError) => {
                switch (error.status) {
                    case ClientEntitiesResolverErrorStatesEnum.GetCountriesFailed:
                        return ClientEntitiesMessages.GetCountriesFailed.message;
                    case ClientEntitiesResolverErrorStatesEnum.GetCountriesInvalidData:
                        return ClientEntitiesMessages.GetCountriesInvalidData.message;
                    case ClientEntitiesResolverErrorStatesEnum.UserSearchFailed:
                        return ClientEntitiesMessages.UserSearchFailed.message;
                    case ClientEntitiesResolverErrorStatesEnum.GetRolesFailed:
                        return ClientEntitiesMessages.GetRolesFailed.message;
                    case ClientEntitiesResolverErrorStatesEnum.GetTradingPlatformsFailed:
                        return ClientEntitiesMessages.GetTradingPlatformsFailed.message;
                }
            });

            throw new InitialComponentDataValidationException(messages);
        }
    }

    onRowClicked(id: string | number, subPanelPath?: string): void {
        const identifiers = (id as string).split(ClientsReportRowIdSeparator);

        const enrollId = identifiers[0] ? parseInt(identifiers[0], 10) : 0;
        const enrollType = identifiers[1] ? identifiers[1] : '';

        const selectedClients = this.modelsCollection.filter((item: IndividualReportModel) => {
            return (item.enrollId === enrollId) && (item.enrollType === enrollType);
        });

        if (selectedClients.length > 0) {
            const clientPath = (selectedClients[0].enrollType === EnrollTypesEnum.Individual) ? IndividualPathSelector :
                CompaniesPathSelector;

            this.router.navigate([[this.panelPaths.root, subPanelPath, enrollId, clientPath].join('/')])
                .finally();
        }
    }

    onExportClicked(): void {
        const filterColumns = this.visibleTableColumns.reduce((
            headerColumns: ExportableFilterHeaderColumnsInterface[],
            column: ReportTableColumn
        ) => {
            const headerColumn: any = {};

            headerColumn.name = column.label;

            switch (column.selector) {
                case IndividualReportModel.getEnrollDateSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getEnrollDateCustomSelector();
                    break;
                case IndividualReportModel.getStatusNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getStatusSelector();
                    break;
                case IndividualReportModel.getEnrollTypeSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getEnrollTypeSelector();
                    break;
                case IndividualReportModel.getNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getNameCustomSelector();
                    break;
                case IndividualReportModel.getCountryNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getCountryIdSelector();
                    break;
                case IndividualReportModel.getTypeBehaviorNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getTypeBehaviourSelector();
                    break;
                case IndividualReportModel.getVerificationDateSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getVerificationDateCustomSelector();
                    break;
                case IndividualReportModel.getPlatformSubscriptionListSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getPlatformSubscriptionsSelector();
                    break;
                case IndividualReportModel.getResidentialCountryNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getResidentialCountryIdSelector();
                    break;
                case IndividualReportModel.getEnrollTradingPlatformNameSelector():
                    headerColumn.corresponding_to_response_model = SearchUsersExportFilterModel.getEnrollTradingPlatformName();
                    break;
                default:
                    headerColumn.corresponding_to_response_model = column.selector;
                    break;
            }

            headerColumns.push(headerColumn);

            return headerColumns;
        }, []);

        const filter = new SearchUsersExportFilterModel(filterColumns, this.getActiveClientExportFilter());

        this.getExportFilterDataSubscription = this.reportsService.getSearchUserExportReport(filter).subscribe((
            response: ServerResponseData<Blob>
        ) => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                const alertMessage = (response.status !== HttpStatusCodesEnum.NotFound) ? ClientEntitiesMessages.UnknownError.message
                    : ClientEntitiesMessages.NotFoundExportData.message;

                this.alertsService.addAlert(alertMessage);

                return false;
            }

            const blob = (response.data as Blob);
            const fileName = `${this.getTitle()}_${moment().format(environment.dateFormatLocalDate)}.${FileExtensionsEnum.Csv}`;

            Helper.filesHelper().triggerDownloadFile(fileName, blob);
        });
    }

    toggleColumns(tableColumns: ReportTableColumn[]): void {
        this.visibleTableColumns = tableColumns;
    }

    onMifirIndividualExportClicked(): void {
        this.triggerMifirClientsExport(EnrollTypesEnum.Individual);
    }

    onMifirCorporateExportClicked(): void {
        this.triggerMifirClientsExport(EnrollTypesEnum.Corporate);
    }

    sortData(column: SortColumnInterface): void {
        const columnSelector = this.fixSortColumnSelector(column.selector);

        this.sortedColumn = column;
        this.filterModel.setOrderColumn(columnSelector);
        this.filterModel.setOrderAscending(this.sortedColumn.ascending);

        super.forceFilterData(this.filterModel);
    }
}
